/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 */

import type { CalendarEvent } from '@repo/common/types/Calendar';
import type {
  StoredCalendarEvent,
  StoredCalendarEventCreateInput,
  StoredCalendarEventModel,
  StoredCalendarEventUpdateInput
} from '@repo/common/types/StoredCalendarEvent';
import { derived, get, writable, type Readable } from 'svelte/store';
import { getHandler } from '../sharedlib';

export interface CalendarEventStore {
  events: Map<string, StoredCalendarEvent>;
  loading: boolean;
  error: string | null;
  lastSync: Date | null;
}

const createCalendarEventStore = () => {
  const internalStore = writable<CalendarEventStore>({
    events: new Map(),
    loading: false,
    error: null,
    lastSync: null
  });

  const { subscribe, set: _set, update } = internalStore;

  let calendarEventModel: StoredCalendarEventModel | null = null;
  let syncInProgress = false;

  const initializeCalendarEventModel = async () => {
    if (!calendarEventModel) {
      try {
        const handler = getHandler();
        calendarEventModel = await handler.getCalendarEventModel();
      } catch (err) {
        console.warn('Failed to initialize calendar event model:', err);
        throw err;
      }
    }
    return calendarEventModel;
  };

  const refreshCalendarEvents = async () => {
    if (syncInProgress) {
      console.log('Calendar event sync already in progress, skipping...');
      return;
    }

    try {
      syncInProgress = true;
      update((store) => ({ ...store, loading: true, error: null }));

      console.log('[CalendarEventStore] Starting refresh...');
      const model = await initializeCalendarEventModel();
      const allEvents = await model.findAllObj({});

      update((store) => ({
        ...store,
        events: new Map(allEvents.map((event) => [event.id, event])),
        loading: false,
        lastSync: new Date()
      }));

      console.log(`[CalendarEventStore] Refresh completed: ${allEvents.length} events loaded`);
    } catch (err: any) {
      if (err?.message?.includes('User ID not set')) {
        console.log('Could not load calendar events - user ID not set');
      } else {
        console.debug('Failed to refresh calendar events:', err);
        update((store) => ({
          ...store,
          loading: false,
          error: err.message
        }));
      }
    } finally {
      syncInProgress = false;
    }
  };

  const createCalendarEvent = async (
    input: StoredCalendarEventCreateInput
  ): Promise<StoredCalendarEvent | null> => {
    try {
      const model = await initializeCalendarEventModel();

      // Create a complete StoredCalendarEvent object
      const event: StoredCalendarEvent = {
        id: input.id || crypto.randomUUID(),
        url: input.url,
        calendarId: input.calendarId,
        workspaceId: input.workspaceId,
        transcriptId: input.transcriptId,
        data: input.data,
        tags: input.tags || [],
        participants: input.participants || [],
        pushedAt: new Date(),
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await model.bulkUpsertItems([event]);

      // Update store immediately for better UX
      setCalendarEvent(event);

      console.log('Created calendar event:', event.id);
      return event;
    } catch (err: any) {
      console.warn('Failed to create calendar event:', err);
      update((store) => ({ ...store, error: err.message }));
      return null;
    }
  };

  const updateCalendarEvent = async (
    id: string,
    updates: StoredCalendarEventUpdateInput
  ): Promise<boolean> => {
    try {
      const model = await initializeCalendarEventModel();
      const existingEvent = getCalendarEvent(id);

      if (!existingEvent) {
        // Create new event if it doesn't exist
        const newEvent = await createCalendarEvent({ id, ...updates });
        return newEvent !== null;
      }

      // Merge updates with existing event
      const updatedEvent: StoredCalendarEvent = {
        ...existingEvent,
        ...updates,
        updatedAt: new Date()
      };

      await model.updateItem(updatedEvent);

      // Update store immediately for better UX
      setCalendarEvent(updatedEvent);

      console.log('Updated calendar event:', id);
      return true;
    } catch (err: any) {
      console.warn('Failed to update calendar event:', err);
      update((store) => ({ ...store, error: err.message }));
      return false;
    }
  };

  const deleteCalendarEvent = async (id: string): Promise<boolean> => {
    try {
      const model = await initializeCalendarEventModel();
      await model.destroyRecords({ id });

      // Update store immediately for better UX
      removeCalendarEvent(id);
      console.log('Deleted calendar event:', id);

      return true;
    } catch (err: any) {
      console.warn('Failed to delete calendar event:', err);
      update((store) => ({ ...store, error: err.message }));
      return false;
    }
  };

  const getCalendarEventsByWorkspace = (workspaceId: string): Readable<StoredCalendarEvent[]> => {
    return derived({ subscribe }, ($store) => {
      return Array.from($store.events.values()).filter(
        (event) => event.workspaceId === workspaceId
      );
    });
  };

  const getCalendarEventsByTranscript = (transcriptId: string): Readable<StoredCalendarEvent[]> => {
    return derived({ subscribe }, ($store) => {
      return Array.from($store.events.values()).filter(
        (event) => event.transcriptId === transcriptId
      );
    });
  };

  const getCalendarEventsByTags = (tags: string[]): Readable<StoredCalendarEvent[]> => {
    return derived({ subscribe }, ($store) => {
      return Array.from($store.events.values()).filter((event) =>
        event.tags?.some((tag) => tags.includes(tag))
      );
    });
  };

  const getAllCalendarEvents = (): Readable<StoredCalendarEvent[]> => {
    return derived({ subscribe }, ($store) => {
      return Array.from($store.events.values());
    });
  };

  // Helper methods for calendar event management
  const getCalendarEvent = (id: string, eventsMap?: Map<string, StoredCalendarEvent>): StoredCalendarEvent | undefined => {
    return (eventsMap || get(internalStore).events).get(id);
  };

  const setCalendarEvent = (event: StoredCalendarEvent) => {
    update((store) => {
      const newEvents = new Map(store.events);
      newEvents.set(event.id, event);
      return { ...store, events: newEvents };
    });
  };

  const removeCalendarEvent = (id: string) => {
    update((store) => {
      const newEvents = new Map(store.events);
      newEvents.delete(id);
      return { ...store, events: newEvents };
    });
  };

  const updateCalendarEventInStore = (id: string, updates: Partial<StoredCalendarEvent>) => {
    update((store) => {
      const event = store.events.get(id);
      if (!event) return store;

      const newEvents = new Map(store.events);
      newEvents.set(id, { ...event, ...updates, updatedAt: new Date() });
      return { ...store, events: newEvents };
    });
  };

  const getCalendarEventsArray = (): Readable<StoredCalendarEvent[]> => {
    return derived({ subscribe }, ($store) => {
      return Array.from($store.events.values());
    });
  };

  // Utility method to enrich calendar events with stored data
  const getEnrichedCalendarEvent = (
    calendarEventId: string,
    baseCalendarEvent?: any,
    eventsMap?: Map<string, StoredCalendarEvent>
  ) => {
    const storedEvent = getCalendarEvent(calendarEventId, eventsMap);
    if (!storedEvent && !baseCalendarEvent) return null;

    // Merge stored data with base calendar event
    return {
      ...baseCalendarEvent,
      // Add stored metadata
      workspaceId: storedEvent?.workspaceId,
      transcriptId: storedEvent?.transcriptId,
      isFavorite: storedEvent?.isFavorite || false,
      storedTags: storedEvent?.tags || [],
      storedParticipants: storedEvent?.participants || [],
      storedData: storedEvent?.data,
      storedUrl: storedEvent?.url,
      // Include the stored event for reference
      _storedEvent: storedEvent
    };
  };

  // Bulk enrichment method for calendar events
  const enrichCalendarEvents = (
    calendarEvents: CalendarEvent[],
    eventsMap?: Map<string, StoredCalendarEvent>
  ): CalendarEvent[] => {
    const eventsToUse = eventsMap || get(internalStore).events;
    console.log(`[CalendarEventStore] Enriching ${calendarEvents.length} calendar events`);

    return calendarEvents.map((event) => {
      const eventId = event.uid;
      if (!eventId) return event;

      const enrichedEvent = getEnrichedCalendarEvent(eventId, event, eventsToUse) || event;
      return enrichedEvent;
    });
  };

  return {
    subscribe,
    refreshCalendarEvents,
    createCalendarEvent,
    updateCalendarEvent,
    deleteCalendarEvent,
    getCalendarEventsByWorkspace,
    getCalendarEventsByTranscript,
    getCalendarEventsByTags,
    getAllCalendarEvents,
    getCalendarEvent,
    setCalendarEvent,
    removeCalendarEvent,
    updateCalendarEventInStore,
    getCalendarEventsArray,
    getEnrichedCalendarEvent,
    enrichCalendarEvents
  };
};

export const calendarEventStore = createCalendarEventStore();
